import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { LogOut, Package } from "lucide-react";
import Logo from "./Logo";
import { API } from "@/config/site";

const inputCls =
  "w-full bg-[#0A0A0A] border border-[#262626] focus:border-[#E60000] outline-none px-4 py-3 font-mono text-sm text-white placeholder:text-[#555] transition-colors";

export default function AccountPage() {
  const [user, setUser] = useState(undefined);
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [orders, setOrders] = useState([]);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const loadOrders = useCallback(async () => {
    const { data } = await axios.get(`${API}/account/orders`, { withCredentials: true });
    setOrders(data);
  }, []);

  useEffect(() => {
    axios
      .get(`${API}/auth/me`, { withCredentials: true })
      .then((r) => {
        setUser(r.data);
        loadOrders().catch(() => {});
      })
      .catch(() => setUser(null));
  }, [loadOrders]);

  const submit = async () => {
    setBusy(true);
    setError("");
    try {
      const payload =
        mode === "login" ? { email: form.email, password: form.password } : form;
      const { data } = await axios.post(`${API}/auth/${mode}`, payload, {
        withCredentials: true,
      });
      setUser(data);
      loadOrders().catch(() => {});
      toast.success(mode === "login" ? "Welcome back." : "Account created.");
    } catch (e) {
      const d = e.response?.data?.detail;
      setError(
        typeof d === "string"
          ? d
          : Array.isArray(d)
            ? d.map((x) => x.msg).join(" ")
            : "Something went wrong"
      );
    } finally {
      setBusy(false);
    }
  };

  const logout = async () => {
    await axios.post(`${API}/auth/logout`, {}, { withCredentials: true }).catch(() => {});
    setUser(null);
    setOrders([]);
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <nav className="flex items-center justify-between px-5 sm:px-10 py-6 border-b border-[#1a1a1a]">
        <Logo />
        {user && (
          <button
            onClick={logout}
            data-testid="account-logout-btn"
            aria-label="Log out"
            className="text-[#888] hover:text-[#E60000] transition-colors"
          >
            <LogOut size={18} />
          </button>
        )}
      </nav>

      {user === undefined && (
        <p className="font-mono text-xs text-[#888] text-center py-24 tracking-[0.3em]">
          CHECKING SESSION...
        </p>
      )}

      {user === null && (
        <div className="max-w-sm mx-auto px-5 py-20">
          <h1 className="font-display uppercase text-4xl" data-testid="account-title">
            {mode === "login" ? "Sign in" : "Create account"}
          </h1>
          <p className="font-mono text-xs text-[#888] mt-2">
            Track your orders and check out faster.
          </p>
          <div className="grid grid-cols-2 gap-2 mt-8">
            <button
              onClick={() => setMode("login")}
              data-testid="account-login-tab"
              className={`border px-3 py-3 font-mono text-xs tracking-[0.1em] uppercase transition-colors ${
                mode === "login"
                  ? "border-[#E60000] text-[#E60000] bg-[#E60000]/10"
                  : "border-[#262626] text-[#999]"
              }`}
            >
              Sign in
            </button>
            <button
              onClick={() => setMode("register")}
              data-testid="account-register-tab"
              className={`border px-3 py-3 font-mono text-xs tracking-[0.1em] uppercase transition-colors ${
                mode === "register"
                  ? "border-[#E60000] text-[#E60000] bg-[#E60000]/10"
                  : "border-[#262626] text-[#999]"
              }`}
            >
              Register
            </button>
          </div>
          <div className="mt-4 space-y-4">
            {mode === "register" && (
              <input
                data-testid="account-name-input"
                className={inputCls}
                placeholder="NAME"
                value={form.name}
                onChange={(e) => set("name", e.target.value)}
              />
            )}
            <input
              data-testid="account-email-input"
              type="email"
              className={inputCls}
              placeholder="EMAIL"
              value={form.email}
              onChange={(e) => set("email", e.target.value)}
            />
            <input
              data-testid="account-password-input"
              type="password"
              className={inputCls}
              placeholder={mode === "register" ? "PASSWORD (8+ CHARACTERS)" : "PASSWORD"}
              value={form.password}
              onChange={(e) => set("password", e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && submit()}
            />
            {error && (
              <p data-testid="account-error" className="font-mono text-xs text-[#E60000]">
                {error}
              </p>
            )}
            <button
              onClick={submit}
              disabled={busy}
              data-testid="account-submit-btn"
              className="w-full bg-[#E60000] text-white font-display uppercase tracking-wide text-lg py-4 hover:bg-white hover:text-black transition-colors disabled:opacity-50"
            >
              {busy ? "One sec..." : mode === "login" ? "Sign in" : "Create account"}
            </button>
          </div>
        </div>
      )}

      {user && (
        <main className="max-w-3xl mx-auto px-5 py-12" data-testid="account-page">
          <p className="font-mono text-xs tracking-[0.35em] text-[#E60000] uppercase">Account</p>
          <h1 className="font-display uppercase text-4xl sm:text-5xl mt-2">
            {user.name || "Your orders"}
          </h1>
          <p className="font-mono text-xs text-[#888] mt-2">{user.email}</p>

          <h2 className="font-display uppercase text-2xl mt-12 flex items-center gap-2">
            <Package size={20} className="text-[#E60000]" /> My orders
          </h2>
          <div className="mt-4 space-y-3" data-testid="account-orders">
            {orders.length === 0 && (
              <p className="font-mono text-xs text-[#555] border border-[#262626] p-6" data-testid="account-orders-empty">
                No orders on this email yet. Cop something from the drop.
              </p>
            )}
            {orders.map((o) => (
              <div
                key={o.order_id}
                data-testid={`account-order-${o.order_id}`}
                className="border border-[#262626] bg-[#121212] p-4 flex flex-wrap items-center justify-between gap-3"
              >
                <div>
                  <p className="font-mono text-sm text-white">
                    {o.quantity} x {o.product_name} ({o.pack_label})
                  </p>
                  <p className="font-mono text-[10px] text-[#666] mt-1 uppercase tracking-[0.15em]">
                    {new Date(o.created_at).toLocaleDateString()} - {o.payment_method} - $
                    {o.total}
                  </p>
                </div>
                <span
                  data-testid={`account-order-status-${o.order_id}`}
                  className={`px-2 py-1 font-mono text-[10px] tracking-[0.15em] uppercase ${
                    o.status === "shipped"
                      ? "bg-white text-black"
                      : o.status === "verified"
                        ? "bg-[#E60000] text-white"
                        : "border border-[#333] text-[#888]"
                  }`}
                >
                  {o.status === "verified" ? "Paid" : o.status}
                </span>
              </div>
            ))}
          </div>
        </main>
      )}
    </div>
  );
}
